import { useContext, useEffect, useCallback } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

/**
 * Blocks navigation if 'when' is true.
 * Works with React Router v6.x (especially <= 6.3.0)
 */
export function useBlocker(blocker: (tx: any) => void, when = true) {
  const { navigator } = useContext(NavigationContext) as any;

  useEffect(() => {
    if (!when) return;

    const unblock = navigator.block((tx: any) => {
      const autoUnblockTx = {
        ...tx,
        retry() {
          unblock();
          tx.retry();
        }
      };

      blocker(autoUnblockTx);
    });

    return unblock;
  }, [navigator, blocker, when]);
}

/**
 * Prompts the user when they try to navigate away if 'when' is true.
 */
export function usePrompt(message: string, when = true, shouldBlock: (nextLocation: any) => boolean = () => true) {
  const blocker = useCallback(
    (tx: any) => {
      // Check if we should actually block this specific navigation
      if (shouldBlock(tx.location)) {
        if (window.confirm(message)) {
          tx.retry();
        }
      } else {
        tx.retry();
      }
    },
    [message, shouldBlock]
  );

  useBlocker(blocker, when);

  useEffect(() => {
    if (!when) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [message, when]);
}
